/**
 * Extended error class thrown by ErrorInterceptor.
 * Preserves structured fields from API error responses (e.g. rateLimitResetAt)
 * while remaining compatible with plain Error (message still works).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly errorCode?: string | null,
    public readonly rateLimitResetAt?: string | null,
    /** Reason code from the API's auth responses (e.g. `idle_expired`), when it sent one. */
    public readonly reason?: string | null,
    /** Server trace id, so a console report can be matched to a server log line. */
    public readonly traceId?: string | null,
    /** True when the API said the session itself is over, as opposed to this request failing. */
    public readonly sessionExpired: boolean = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Alias for {@link statusCode}.
   *
   * `HttpErrorResponse` calls this `status`, and code that classifies an error reaches for `status`
   * by habit — `AuthInterceptor` did, which is why its entire 401 recovery path was unreachable:
   * `undefined === 401` is false, so a 401 that had already passed through this class was never
   * recognised as one. Keeping both names means a misremembered field name cannot silently disable
   * error handling again.
   */
  get status(): number {
    return this.statusCode;
  }
}
